import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Briefcase, Plus, Pencil, Trash2, GripVertical, Eye, EyeOff,
  ChevronUp, ChevronDown, Save, X, Users, UserPlus, GraduationCap,
  DollarSign, Calculator, Target, Cpu, Building2, FileText, Compass,
  ChevronRight, CornerDownRight, Linkedin, MessageSquare, TrendingUp,
  ArrowRightLeft, Heart, Star, Award, BookOpen, Lightbulb
} from 'lucide-react';

const API = '/api';

// Icon mapping for services
const ICON_OPTIONS = [
  { value: 'Briefcase', label: 'Briefcase', icon: Briefcase },
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'UserPlus', label: 'User Plus', icon: UserPlus },
  { value: 'GraduationCap', label: 'Graduation', icon: GraduationCap },
  { value: 'DollarSign', label: 'Dollar Sign', icon: DollarSign },
  { value: 'Calculator', label: 'Calculator', icon: Calculator },
  { value: 'Target', label: 'Target', icon: Target },
  { value: 'Cpu', label: 'Technology', icon: Cpu },
  { value: 'Building2', label: 'Building', icon: Building2 },
  { value: 'FileText', label: 'Document', icon: FileText },
  { value: 'Compass', label: 'Compass', icon: Compass },
  { value: 'Linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'MessageSquare', label: 'Message', icon: MessageSquare },
  { value: 'TrendingUp', label: 'Trending Up', icon: TrendingUp },
  { value: 'ArrowRightLeft', label: 'Transition', icon: ArrowRightLeft },
  { value: 'Heart', label: 'Heart', icon: Heart },
  { value: 'Star', label: 'Star', icon: Star },
  { value: 'Award', label: 'Award', icon: Award },
  { value: 'BookOpen', label: 'Book', icon: BookOpen },
  { value: 'Lightbulb', label: 'Lightbulb', icon: Lightbulb },
];

const getIconComponent = (iconName) => {
  const found = ICON_OPTIONS.find(i => i.value === iconName);
  return found ? found.icon : Briefcase;
};

export const AdminCMSServicesPage = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    description_en: '',
    description_ar: '',
    short_description_en: '',
    short_description_ar: '',
    icon: 'Briefcase',
    parent_service_id: null,
    is_active: true,
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/cms/services`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  // Get top-level and sub-services organized
  const topLevelServices = services.filter(s => !s.parent_service_id);
  const getSubServices = (parentId) => services.filter(s => s.parent_service_id === parentId);

  const handleOpenDialog = (service = null, parentId = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        title_en: service.title_en || '',
        title_ar: service.title_ar || '',
        description_en: service.description_en || '',
        description_ar: service.description_ar || '',
        short_description_en: service.short_description_en || '',
        short_description_ar: service.short_description_ar || '',
        icon: service.icon || 'Briefcase',
        parent_service_id: service.parent_service_id || null,
        is_active: service.is_active !== false,
      });
    } else {
      setEditingService(null);
      setFormData({
        title_en: '',
        title_ar: '',
        description_en: '',
        description_ar: '',
        short_description_en: '',
        short_description_ar: '',
        icon: 'Briefcase',
        parent_service_id: parentId,
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title_en.trim()) {
      toast.error('English title is required');
      return;
    }

    try {
      const url = editingService 
        ? `${API}/admin/cms/services/${editingService.service_id}`
        : `${API}/admin/cms/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingService ? 'Service updated' : 'Service created');
        setDialogOpen(false);
        fetchServices();
      } else {
        toast.error('Failed to save service');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    }
  };

  const handleDelete = async () => {
    if (!serviceToDelete) return;

    try {
      const response = await fetch(`${API}/admin/cms/services/${serviceToDelete.service_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        toast.success('Service deleted');
        setDeleteDialogOpen(false);
        setServiceToDelete(null);
        fetchServices();
      } else {
        toast.error('Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleToggleActive = async (service) => {
    try {
      const response = await fetch(`${API}/admin/cms/services/${service.service_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ is_active: !service.is_active })
      });

      if (response.ok) {
        toast.success(service.is_active ? 'Service hidden' : 'Service visible');
        fetchServices();
      }
    } catch (error) {
      console.error('Error toggling service:', error);
    }
  };

  const handleMoveService = async (service, direction) => {
    const currentIndex = services.findIndex(s => s.service_id === service.service_id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= services.length) return;

    const newServices = [...services];
    [newServices[currentIndex], newServices[newIndex]] = [newServices[newIndex], newServices[currentIndex]];
    
    const orders = newServices.map((s, i) => ({ service_id: s.service_id, order: i + 1 }));

    try {
      const response = await fetch(`${API}/admin/cms/services/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(orders)
      });

      if (response.ok) {
        fetchServices();
      }
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {language === 'ar' ? 'إدارة الخدمات' : 'Services Management'}
          </h1>
          <p className="text-[#6B7280] mt-1">
            {language === 'ar' 
              ? 'إدارة الخدمات المعروضة على الموقع العام'
              : 'Manage services displayed on the public website'}
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-[#33AADD] hover:bg-[#2b8eb8]" data-testid="add-service-btn">
          <Plus className="h-4 w-4 me-2" />
          {language === 'ar' ? 'إضافة خدمة' : 'Add Service'}
        </Button>
      </div>

      {/* Services List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-8 text-[#6B7280]">Loading...</div>
        ) : services.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-[#6B7280]">
              No services found. Add your first service.
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Top Level Services */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
                {language === 'ar' ? 'الخدمات الرئيسية' : 'Main Services'}
              </h3>
            </div>
            {topLevelServices.map((service, index) => {
              const IconComponent = getIconComponent(service.icon);
              const subServices = getSubServices(service.service_id);
              return (
                <div key={service.service_id}>
                  <Card 
                    className={`transition-all ${!service.is_active ? 'opacity-60 bg-gray-50' : ''}`}
                    data-testid={`service-card-${index}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Reorder Controls */}
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleMoveService(service, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleMoveService(service, 'down')}
                            disabled={index === topLevelServices.length - 1}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Icon */}
                        <div className="h-12 w-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-6 w-6 text-[#33AADD]" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-[#111827]">{service.title_en}</h3>
                            {subServices.length > 0 && (
                              <Badge variant="outline" className="text-xs">
                                {subServices.length} sub-services
                              </Badge>
                            )}
                            {!service.is_active && (
                              <Badge variant="secondary" className="text-xs">Hidden</Badge>
                            )}
                          </div>
                          {service.title_ar && (
                            <p className="text-sm text-[#6B7280]" dir="rtl">{service.title_ar}</p>
                          )}
                          <p className="text-sm text-[#6B7280] mt-1 line-clamp-1">
                            {service.short_description_en || service.description_en?.slice(0, 100)}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(service)}
                            title={service.is_active ? 'Hide' : 'Show'}
                          >
                            {service.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(service)}
                            data-testid={`edit-service-${index}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setServiceToDelete(service);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Sub-services */}
                  {subServices.length > 0 && (
                    <div className="ms-8 mt-2 space-y-2 mb-3">
                      {subServices.map((sub, subIndex) => {
                        const SubIconComponent = getIconComponent(sub.icon);
                        return (
                          <Card 
                            key={sub.service_id}
                            className={`transition-all border-l-4 border-l-[#33AADD]/30 ${!sub.is_active ? 'opacity-60 bg-gray-50' : ''}`}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-center gap-3">
                                <CornerDownRight className="h-4 w-4 text-[#33AADD]/50" />
                                <div className="h-8 w-8 rounded-lg bg-[#33AADD]/5 flex items-center justify-center flex-shrink-0">
                                  <SubIconComponent className="h-4 w-4 text-[#33AADD]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-sm text-[#111827]">{sub.title_en}</h4>
                                    {!sub.is_active && (
                                      <Badge variant="secondary" className="text-xs">Hidden</Badge>
                                    )}
                                  </div>
                                  {sub.title_ar && (
                                    <p className="text-xs text-[#6B7280]" dir="rtl">{sub.title_ar}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => handleToggleActive(sub)}
                                  >
                                    {sub.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => handleOpenDialog(sub)}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                    onClick={() => {
                                      setServiceToDelete(sub);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(null, service.service_id)}
                        className="ms-6 text-[#33AADD] border-dashed"
                      >
                        <Plus className="h-3 w-3 me-1" />
                        Add Sub-service
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingService 
                ? (language === 'ar' ? 'تعديل الخدمة' : 'Edit Service')
                : (language === 'ar' ? 'إضافة خدمة جديدة' : 'Add New Service')}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'أدخل تفاصيل الخدمة بالإنجليزية والعربية'
                : 'Enter service details in English and Arabic'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Parent Service Selection */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الخدمة الأم' : 'Parent Service'}</Label>
              <Select 
                value={formData.parent_service_id || 'none'} 
                onValueChange={(v) => setFormData({...formData, parent_service_id: v === 'none' ? null : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {language === 'ar' ? 'بدون (خدمة رئيسية)' : 'None (Top-level service)'}
                  </SelectItem>
                  {topLevelServices
                    .filter(s => s.service_id !== editingService?.service_id)
                    .map(s => (
                      <SelectItem key={s.service_id} value={s.service_id}>
                        {s.title_en}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6B7280]">
                {language === 'ar' 
                  ? 'اختر خدمة رئيسية لإضافة هذه كخدمة فرعية'
                  : 'Select a parent to make this a sub-service'}
              </p>
            </div>

            {/* Icon Selection */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الأيقونة' : 'Icon'}</Label>
              <Select value={formData.icon} onValueChange={(v) => setFormData({...formData, icon: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* English Title */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'العنوان (إنجليزي) *' : 'Title (English) *'}</Label>
              <Input
                value={formData.title_en}
                onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                placeholder="e.g., HR Consulting"
                data-testid="service-title-en"
              />
            </div>

            {/* Arabic Title */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'العنوان (عربي)' : 'Title (Arabic)'}</Label>
              <Input
                value={formData.title_ar}
                onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                placeholder="مثال: استشارات الموارد البشرية"
                dir="rtl"
                data-testid="service-title-ar"
              />
            </div>

            {/* Short Description EN */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'وصف مختصر (إنجليزي)' : 'Short Description (English)'}</Label>
              <Input
                value={formData.short_description_en}
                onChange={(e) => setFormData({...formData, short_description_en: e.target.value})}
                placeholder="Brief tagline for cards"
              />
            </div>

            {/* Short Description AR */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'وصف مختصر (عربي)' : 'Short Description (Arabic)'}</Label>
              <Input
                value={formData.short_description_ar}
                onChange={(e) => setFormData({...formData, short_description_ar: e.target.value})}
                placeholder="وصف مختصر للبطاقات"
                dir="rtl"
              />
            </div>

            {/* Full Description EN */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الوصف الكامل (إنجليزي)' : 'Full Description (English)'}</Label>
              <Textarea
                value={formData.description_en}
                onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                placeholder="Detailed service description..."
                rows={3}
              />
            </div>

            {/* Full Description AR */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الوصف الكامل (عربي)' : 'Full Description (Arabic)'}</Label>
              <Textarea
                value={formData.description_ar}
                onChange={(e) => setFormData({...formData, description_ar: e.target.value})}
                placeholder="وصف تفصيلي للخدمة..."
                dir="rtl"
                rows={3}
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>{language === 'ar' ? 'مرئي على الموقع' : 'Visible on Website'}</Label>
                <p className="text-xs text-[#6B7280]">
                  {language === 'ar' ? 'عند التعطيل، لن تظهر الخدمة للزوار' : 'When disabled, service will be hidden from visitors'}
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} className="bg-[#33AADD] hover:bg-[#2b8eb8]">
              <Save className="h-4 w-4 me-2" />
              {language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'ar' ? 'حذف الخدمة' : 'Delete Service'}</DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? `هل أنت متأكد من حذف "${serviceToDelete?.title_en}"؟ لا يمكن التراجع عن هذا الإجراء.`
                : `Are you sure you want to delete "${serviceToDelete?.title_en}"? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 me-2" />
              {language === 'ar' ? 'حذف' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCMSServicesPage;
